import { Component, OnInit, ViewChild, TemplateRef, inject, computed } from '@angular/core';
import { DownloadResponse, DownloadsService } from './downloads.service';
import { NotificationService } from '@app/services/notifications.service';
import { CategoryType, DepartmentType, CATEGORY_LABELS, DEPARTMENT_LABELS, DEPARTMENT_CATEGORY_RELATION } from './download-enums';
import {
  formatDownloadDateShort,
  getCategoryIconChar,
  openUrlInNewTab,
  sortDownloadsNewestFirst,
} from './downloads-display';
import { BaseComponent } from '@app/components/base/base.component';
import { ActivatedRoute } from '@angular/router';
import { MenusServices } from '@app/services/menus.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer, switchMap } from 'rxjs';
import { createLoadingTracker } from '@app/core/util/loading-tracker';

interface PageEvent {
  pageIndex: number;
  pageSize: number;
}

@Component({
  selector: 'app-downloads',
  templateUrl: './downloads.component.html',
  styleUrl: './downloads.component.scss',
})
export class DownloadsComponent extends BaseComponent implements OnInit {
  private readonly downloadsService = inject(DownloadsService);
  private readonly loading = createLoadingTracker();
  readonly isLoading = computed(() => this.loading.count() > 0);

  @ViewChild('template') template: TemplateRef<any>;
  resetSelectTrigger = false;

  isGridView = true;
  hasData = false;

  downloads: DownloadResponse[] = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 20;

  filters: {
    page: number;
    pageSize: number;
    departmentType?: DepartmentType;
    categoryType?: CategoryType;
    originalName?: string;
    description?: string;
    startDate?: Date;
    endDate?: Date;
  } = {
    page: 1,
    pageSize: 20,
  };

  private tempFilters: Partial<typeof this.filters> = {};

  categoryOptions = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
    label,
    value: value as CategoryType,
  }));

  departmentOptions = Object.entries(DEPARTMENT_LABELS).map(([value, label]) => ({
    label,
    value: value as DepartmentType,
  }));

  readonly categoryLabels = CATEGORY_LABELS;
  readonly departmentLabels = DEPARTMENT_LABELS;

  module = '';

  constructor(
    private notificationService: NotificationService,
    private activeRoute: ActivatedRoute,
    private menu: MenusServices
  ) {
    super(activeRoute, menu, notificationService);
    this.module = this.sendDataToMenu();
  }

  /** Valores mostrados en filtros (borrador hasta «Buscar») */
  get filterTitleDraft(): string {
    return this.tempFilters.originalName ?? this.filters.originalName ?? '';
  }

  get filterDescriptionDraft(): string {
    return this.tempFilters.description ?? this.filters.description ?? '';
  }

  get filterDateFromDraft(): Date | undefined {
    return this.tempFilters.startDate ?? this.filters.startDate;
  }

  get filterDateToDraft(): Date | undefined {
    return this.tempFilters.endDate ?? this.filters.endDate;
  }

  ngOnInit() {
    this.trackerService.track('UserLoadDownloads', {});
    this.loadDownloads();
  }

  loadDownloads() {
    this.loading
      .track(this.downloadsService.getAllDownloads(this.filters))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.downloads = sortDownloadsNewestFirst(response.data);
          this.totalItems = response.totalElements;
          this.currentPage = (response.page ?? 0) + 1;
          this.filters.page = this.currentPage;
          this.pageSize = response.pageSize;
          this.hasData = this.downloads.length > 0;
        },
        error: (error) => {
          console.error('Error al cargar descargas:', error);
          this.notificationService.showMessage(['Error al cargar las descargas'], 'error');
          timer(5000)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
              this.notificationService.clearMessages();
            });
          this.hasData = false;
        },
      });
  }

  onDepartmentChange(departmentType: DepartmentType) {
    const departmentRelation = DEPARTMENT_CATEGORY_RELATION.find(
      (relation) => relation.departmentType === departmentType
    );

    if (departmentRelation) {
      this.categoryOptions = departmentRelation.categorieTypes.map((ct) => ({
        label: CATEGORY_LABELS[ct],
        value: ct,
      }));
    }

    const pendingCategory = this.tempFilters.categoryType ?? this.filters.categoryType;
    if (pendingCategory && departmentRelation && !departmentRelation.categorieTypes.includes(pendingCategory)) {
      this.tempFilters = { ...this.tempFilters, categoryType: undefined };
    }

    this.tempFilters = {
      ...this.tempFilters,
      departmentType,
      categoryType: undefined,
    };
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.filters.page = this.currentPage;
    this.filters.pageSize = this.pageSize;
    this.loadDownloads();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.filters.pageSize = pageSize;
    this.filters.page = 1;
    this.loadDownloads();
  }

  onFilterChange(filters: Partial<typeof this.filters>) {
    this.tempFilters = {
      ...this.tempFilters,
      ...filters,
    };
  }

  onDateChange(field: 'startDate' | 'endDate', date: Date | null) {
    this.tempFilters[field] = date || undefined;
  }

  search() {
    this.filters = {
      ...this.filters,
      ...this.tempFilters,
      page: 1,
    };
    this.loadDownloads();
  }

  clearFilters() {
    this.tempFilters = {};

    this.filters = {
      page: 1,
      pageSize: this.pageSize,
      departmentType: undefined,
      categoryType: undefined,
      originalName: undefined,
      description: undefined,
      startDate: undefined,
      endDate: undefined,
    };

    this.categoryOptions = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
      label,
      value: value as CategoryType,
    }));

    this.resetSelectTrigger = true;
    timer(100)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.resetSelectTrigger = false;
      });

    this.loadDownloads();
  }

  handleDownloadFile(downloadId: number) {
    this.trackerService.track('UserDownloadFile', {
      downloadId,
    });
    this.loading.begin();
    this.downloadsService
      .getDownloadPublicUrl(downloadId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((url) => {
          openUrlInNewTab(url);
          return this.downloadsService.registerDownloadAttempt(downloadId);
        })
      )
      .subscribe({
        error: (error) => {
          this.trackerService.track('ApiErrorRegisterDownload', {
            downloadId: downloadId ?? '',
            error: error.message,
          });
          console.error('Error al obtener o registrar la descarga:', error);
          this.loading.end();
        },
        complete: () => {
          this.loading.end();
        },
      });
  }

  switchView(isGrid: boolean) {
    this.trackerService.track('UserSwitchViewDownloads', {
      view: isGrid ? 'grid' : 'list',
    });
    this.isGridView = isGrid;
  }

  formatDate(date: Date): string {
    return formatDownloadDateShort(date);
  }

  getCategoryLabel(type?: string): string {
    if (!type) return 'Sin categoría';
    return CATEGORY_LABELS[type as keyof typeof CATEGORY_LABELS] || type;
  }

  getDepartmentLabel(type?: string): string {
    if (!type) return 'Sin departamento';
    return DEPARTMENT_LABELS[type as keyof typeof DEPARTMENT_LABELS] || type;
  }

  getCategoryIcon(type: CategoryType | undefined): string {
    return getCategoryIconChar(type);
  }
}
